/**
 * Teams Hub — Create, browse, and manage lifting crews.
 * Tabs: MY TEAMS | RANKINGS | BROWSE
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, FlatList, TouchableOpacity, TextInput,
  ActivityIndicator, Modal, Pressable, Alert, Platform, RefreshControl, Share,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import {
  ArrowLeft, Users, Plus, Shield, Crown, UserPlus, LogOut, Search,
  ChevronDown, ChevronUp, Trophy, TrendingUp, Flame as FlameIcon,
  Share2, Swords, ChevronRight,
} from 'lucide-react-native';
import {
  createTeam, fetchMyTeams, fetchTeamMembers, joinTeam, leaveTeam, searchTeams,
  Team, TeamMember,
} from '@/lib/teams';
import {
  fetchTeamLeaderboard, getMyTeams, generateTeamInviteLink,
  getTeamMemberStrengths, challengeTeam,
  type TeamRanking, type TeamMemberStrength,
} from '@/lib/teamLeaderboard';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast, type Tier } from '@/lib/subscription';
import {
  getTeamLeaderboard, getTeamStats,
  TeamLeaderboardEntry, TeamStats,
} from '@/lib/teamChallenges';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const MEDAL_COLORS = {
  1: '#FFD700', // gold
  2: '#C0C0C0', // silver
  3: '#CD7F32', // bronze
} as Record<number, string>;

type Tab = 'mine' | 'rankings' | 'browse';

/* ------------------------------------------------------------------ */
/*  Screen                                                             */
/* ------------------------------------------------------------------ */

export default function TeamsScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();
  const { tier } = useSubscription();
  const isPro = hasAtLeast(tier, 'pro');
  const isCoach = hasAtLeast(tier, 'coach_pro');

  /* tabs */
  const [activeTab, setActiveTab] = useState<Tab>('mine');

  /* my teams (teams.ts full Team objects) */
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  /* my teams roles from teamLeaderboard.ts */
  const [myTeamRoles, setMyTeamRoles] = useState<Record<string, string>>({});

  /* browse */
  const [browseTeams, setBrowseTeams] = useState<Team[]>([]);
  const [query, setQuery] = useState('');

  /* loading */
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /* rankings */
  const [rankings, setRankings] = useState<TeamRanking[]>([]);
  const [loadingRankings, setLoadingRankings] = useState(false);

  /* team detail modal */
  const [detailTeam, setDetailTeam] = useState<Team | TeamRanking | null>(null);
  const [detailMembers, setDetailMembers] = useState<TeamMemberStrength[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  /* create modal */
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdInviteLink, setCreatedInviteLink] = useState<string | null>(null);

  /* old expanded (browse) */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null);
  const [teamLeaderboard, setTeamLeaderboard] = useState<TeamLeaderboardEntry[]>([]);

  /* ---- helpers ---- */
  const myTeamIds = new Set(myTeams.map(t => t.id));

  const load = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const [mine, browse, roleData] = await Promise.all([
        fetchMyTeams(profile.id),
        searchTeams(query),
        getMyTeams(profile.id),
      ]);
      setMyTeams(mine);
      setBrowseTeams(browse);
      const roleMap: Record<string, string> = {};
      roleData.forEach(r => { roleMap[r.team_id] = r.role; });
      setMyTeamRoles(roleMap);
    } catch { /* silent */ }
    setLoading(false);
  }, [profile?.id, query]);

  useEffect(() => { load(); }, [load]);

  const loadRankings = useCallback(async () => {
    setLoadingRankings(true);
    const data = await fetchTeamLeaderboard();
    setRankings(data);
    setLoadingRankings(false);
  }, []);

  // Load rankings when tab is selected
  useEffect(() => {
    if (activeTab === 'rankings' && rankings.length === 0) {
      loadRankings();
    }
  }, [activeTab]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([
      load(),
      activeTab === 'rankings' ? loadRankings() : Promise.resolve(),
    ]);
    setRefreshing(false);
  }, [load, loadRankings, activeTab]);

  /* ---- team detail ---- */
  const openTeamDetail = async (teamId: string, team: Team | TeamRanking) => {
    setDetailTeam(team);
    setDetailMembers([]);
    setLoadingDetail(true);
    setInviteLink(generateTeamInviteLink(teamId));
    const members = await getTeamMemberStrengths(teamId);
    members.sort((a, b) => b.strength_score - a.strength_score);
    setDetailMembers(members);
    setLoadingDetail(false);
  };

  const closeDetail = () => {
    setDetailTeam(null);
    setDetailMembers([]);
  };

  const shareInviteLink = async (link: string, teamName?: string) => {
    try {
      await Share.share({
        message: `Join ${teamName ?? 'my team'} on DUEL 💪\n${link}`,
        url: link,
        title: 'Join my DUEL team',
      });
    } catch { /* user dismissed */ }
  };

  /* ---- browse expand ---- */
  const toggleExpand = async (teamId: string) => {
    if (expandedId === teamId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(teamId);
    setLoadingMembers(true);
    setTeamStats(null);
    setTeamLeaderboard([]);
    try {
      const [m, stats, lb] = await Promise.all([
        fetchTeamMembers(teamId),
        getTeamStats(teamId),
        getTeamLeaderboard(teamId),
      ]);
      setMembers(m);
      setTeamStats(stats);
      setTeamLeaderboard(lb);
    } catch {
      setMembers([]);
      setTeamStats(null);
      setTeamLeaderboard([]);
    }
    setLoadingMembers(false);
  };

  /* ---- team limits ---- */
  const getTeamLimit = (t: Tier): number | null => {
    if (hasAtLeast(t, 'org_pro')) return null;
    if (hasAtLeast(t, 'coach_pro')) return 3;
    return 1;
  };
  const teamLimit = getTeamLimit(tier);
  const captainTeamCount = myTeams.filter(t => t.captain_id === profile?.id).length;
  const atTeamLimit = teamLimit !== null && captainTeamCount >= teamLimit;

  /* ---- create team ---- */
  const handleCreate = async () => {
    if (!profile?.id || !newName.trim()) return;
    if (atTeamLimit) {
      const upgradeMsg = hasAtLeast(tier, 'coach_pro')
        ? 'Upgrade to Club for unlimited teams.'
        : hasAtLeast(tier, 'pro')
          ? 'Upgrade to Coach (3 teams) or Club (unlimited).'
          : 'Upgrade to Pro or higher to create more teams.';
      Alert.alert(
        'Team Limit Reached',
        `You've created ${captainTeamCount} of ${teamLimit} allowed team${teamLimit === 1 ? '' : 's'}. ${upgradeMsg}`,
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'View Plans', onPress: () => router.push('/paywall' as any) },
        ],
      );
      return;
    }
    setCreating(true);
    try {
      const team = await createTeam(newName, newTag || null, newDesc || null, profile.id);
      const link = generateTeamInviteLink(team.id);
      setCreatedInviteLink(link);
      await load();
    } catch (err: any) {
      const msg = err?.message || 'Failed to create team';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Error', msg);
    }
    setCreating(false);
  };

  const closeCreateModal = () => {
    setShowCreate(false);
    setNewName('');
    setNewTag('');
    setNewDesc('');
    setCreatedInviteLink(null);
  };

  /* ---- actions ---- */
  const handleJoin = async (teamId: string) => {
    if (!profile?.id) return;
    try {
      await joinTeam(teamId, profile.id);
      await load();
    } catch (err: any) {
      const msg = err?.message?.includes('unique') ? 'Already a member' : (err?.message || 'Failed to join');
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Error', msg);
    }
  };

  const handleLeave = async (teamId: string) => {
    if (!profile?.id) return;
    try {
      await leaveTeam(teamId, profile.id);
      setExpandedId(null);
      closeDetail();
      await load();
    } catch (err: any) {
      const msg = err?.message || 'Failed to leave team';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Error', msg);
    }
  };

  /* ================================================================ */
  /*  RENDER: Rankings Tab                                             */
  /* ================================================================ */

  const renderRankingItem = ({ item }: { item: TeamRanking }) => {
    const medalColor = MEDAL_COLORS[item.rank];
    const isTop3 = item.rank <= 3;

    return (
      <TouchableOpacity
        activeOpacity={0.75}
        onPress={() => openTeamDetail(item.team_id, item)}
        style={[
          styles.rankRow,
          {
            backgroundColor: isTop3 ? (medalColor + '18') : c.surface,
            borderColor: isTop3 ? (medalColor + '60') : c.border,
          },
        ]}
      >
        {/* Rank badge */}
        <View style={[
          styles.rankBadge,
          { backgroundColor: medalColor ? medalColor + '30' : c.background, borderColor: medalColor || c.border },
        ]}>
          <Text style={[
            styles.rankNum,
            { color: medalColor || c.textSecondary, fontFamily: TYPO.heading.bold },
          ]}>
            {item.rank}
          </Text>
        </View>

        {/* Team info */}
        <View style={{ flex: 1, gap: 2 }}>
          <Text style={[styles.rankTeamName, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            {item.team_name}
          </Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <Text style={{ color: c.textTertiary, fontSize: 11 }}>
              {item.member_count} members · {item.active_members} active
            </Text>
          </View>
        </View>

        {/* Score */}
        <View style={{ alignItems: 'flex-end', gap: 2 }}>
          <Text style={[styles.rankScore, { color: medalColor || c.text, fontFamily: TYPO.heading.bold }]}>
            {item.avg_strength > 0 ? item.avg_strength : '—'}
          </Text>
          <Text style={{ color: c.textTertiary, fontSize: 10, letterSpacing: 0.5 }}>AVG STR</Text>
        </View>

        <ChevronRight size={14} color={c.textTertiary} style={{ marginLeft: 4 }} />
      </TouchableOpacity>
    );
  };

  /* ================================================================ */
  /*  RENDER: My Teams Tab                                             */
  /* ================================================================ */

  const renderMyTeamCard = (team: Team) => {
    const role = myTeamRoles[team.id] ?? 'member';
    const isCaptain = role === 'captain';
    const isCoCapt = role === 'co_captain';

    return (
      <TouchableOpacity
        key={team.id}
        activeOpacity={0.75}
        onPress={() => openTeamDetail(team.id, team)}
        style={[styles.myTeamCard, { backgroundColor: c.accent + '12', borderColor: c.accent + '40' }]}
      >
        <View style={{ flex: 1, gap: 3 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            {isCaptain ? <Crown size={13} color={c.accent} /> : <Shield size={13} color={c.textSecondary} />}
            <Text style={[styles.myTeamName, { color: c.text, fontFamily: TYPO.heading.bold }]}>
              {team.tag ? `[${team.tag}] ` : ''}{team.name}
            </Text>
          </View>
          <Text style={{ color: isCaptain ? c.accent : c.textSecondary, fontSize: 11, fontFamily: TYPO.body.semibold, letterSpacing: 0.8 }}>
            {isCaptain ? 'CAPTAIN' : isCoCapt ? 'CO-CAPTAIN' : 'MEMBER'}
          </Text>
          {team.description ? (
            <Text style={{ color: c.textTertiary, fontSize: 12 }} numberOfLines={1}>
              {team.description}
            </Text>
          ) : null}
        </View>
        <ChevronRight size={16} color={c.textSecondary} />
      </TouchableOpacity>
    );
  };

  /* ================================================================ */
  /*  RENDER: Browse Tab (existing expand pattern)                     */
  /* ================================================================ */

  const renderBrowseTeamCard = (team: Team) => {
    const isMine = myTeamIds.has(team.id);
    const isCaptain = team.captain_id === profile?.id;
    const expanded = expandedId === team.id;

    return (
      <View key={team.id} style={[
        styles.teamCard,
        {
          backgroundColor: isMine ? c.accent + '10' : c.surface,
          borderColor: isMine ? c.accent + '40' : c.border,
        },
      ]}>
        <TouchableOpacity activeOpacity={0.7} onPress={() => toggleExpand(team.id)}>
          <View style={styles.teamHeader}>
            {isMine ? <Shield size={18} color={c.accent} /> : <Users size={16} color={c.textSecondary} />}
            <View style={{ flex: 1 }}>
              <Text style={[styles.teamName, { color: c.text, fontFamily: TYPO.heading.bold }]}>
                {team.tag ? `[${team.tag}] ` : ''}{team.name}
              </Text>
              {isCaptain && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 }}>
                  <Crown size={11} color={c.accent} />
                  <Text style={{ color: c.accent, fontSize: 11, fontFamily: TYPO.body.semibold }}>CAPTAIN</Text>
                </View>
              )}
            </View>
            {!isMine && (
              <TouchableOpacity
                style={[styles.actionBtn, { borderColor: c.accent }]}
                onPress={() => handleJoin(team.id)}
              >
                <UserPlus size={12} color={c.accent} />
                <Text style={{ color: c.accent, fontSize: 11, fontFamily: TYPO.body.semibold }}>JOIN</Text>
              </TouchableOpacity>
            )}
            {isMine && !isCaptain && (
              <TouchableOpacity
                style={[styles.actionBtn, { borderColor: c.error || '#E53935' }]}
                onPress={() => handleLeave(team.id)}
              >
                <LogOut size={12} color={c.error || '#E53935'} />
                <Text style={{ color: c.error || '#E53935', fontSize: 11, fontFamily: TYPO.body.semibold }}>LEAVE</Text>
              </TouchableOpacity>
            )}
            {expanded ? <ChevronUp size={16} color={c.textSecondary} /> : <ChevronDown size={16} color={c.textSecondary} />}
          </View>
        </TouchableOpacity>

        {team.description && (
          <Text style={[styles.teamDesc, { color: c.textSecondary }]} numberOfLines={expanded ? undefined : 2}>
            {team.description}
          </Text>
        )}

        {expanded && (
          <View style={[styles.membersList, { borderTopColor: c.border }]}>
            {loadingMembers ? (
              <ActivityIndicator color={c.accent} style={{ padding: 12 }} />
            ) : (
              <>
                {teamStats && (
                  <View style={{ gap: 6, marginBottom: 10 }}>
                    <Text style={{ color: c.textSecondary, fontSize: 11, letterSpacing: 1.5, fontFamily: TYPO.body.semibold }}>TEAM STATS</Text>
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      <View style={[styles.statBox, { backgroundColor: c.background, borderColor: c.border }]}>
                        <Trophy size={14} color={c.accent} />
                        <Text style={{ color: c.text, fontSize: 16, fontFamily: TYPO.heading.bold }}>{teamStats.combined_strength}</Text>
                        <Text style={{ color: c.textTertiary, fontSize: 11, letterSpacing: 0.5 }}>COMBINED STR</Text>
                      </View>
                      <View style={[styles.statBox, { backgroundColor: c.background, borderColor: c.border }]}>
                        <FlameIcon size={14} color={c.accent} />
                        <Text style={{ color: c.text, fontSize: 16, fontFamily: TYPO.heading.bold }}>{teamStats.workouts_this_week}</Text>
                        <Text style={{ color: c.textTertiary, fontSize: 11, letterSpacing: 0.5 }}>THIS WEEK</Text>
                      </View>
                      <View style={[styles.statBox, { backgroundColor: c.background, borderColor: c.border }]}>
                        <TrendingUp size={14} color={c.accent} />
                        <Text style={{ color: c.text, fontSize: 16, fontFamily: TYPO.heading.bold }}>{teamStats.average_elo || '—'}</Text>
                        <Text style={{ color: c.textTertiary, fontSize: 11, letterSpacing: 0.5 }}>AVG ELO</Text>
                      </View>
                    </View>
                  </View>
                )}

                {teamLeaderboard.length > 0 && (
                  <View style={{ gap: 4, marginBottom: 10 }}>
                    <Text style={{ color: c.textSecondary, fontSize: 11, letterSpacing: 1.5, fontFamily: TYPO.body.semibold }}>TEAM LEADERBOARD</Text>
                    {teamLeaderboard.map(entry => (
                      <View key={entry.profile_id} style={[styles.memberRow, { paddingVertical: 6 }]}>
                        <Text style={{ color: c.accent, fontSize: 13, fontFamily: TYPO.heading.bold, width: 24 }}>#{entry.rank}</Text>
                        <View style={{ flex: 1 }}>
                          <Text style={{ color: c.text, fontSize: 13, fontFamily: TYPO.body.semibold }}>
                            {entry.display_name || entry.handle || 'Unknown'}
                          </Text>
                        </View>
                        <Text style={{ color: c.text, fontSize: 13, fontFamily: TYPO.heading.bold }}>
                          {entry.strength_score > 0 ? Math.round(entry.strength_score) : '—'}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}

                <Text style={{ color: c.textSecondary, fontSize: 11, letterSpacing: 1.5, fontFamily: TYPO.body.semibold, marginBottom: 2 }}>MEMBERS</Text>
                {members.length === 0 ? (
                  <Text style={{ color: c.textTertiary, fontSize: 12, padding: 8 }}>No members found</Text>
                ) : (
                  members.map(m => (
                    <View key={m.id} style={styles.memberRow}>
                      <View style={{ flex: 1 }}>
                        <Text style={{ color: c.text, fontSize: 13, fontFamily: TYPO.body.semibold }}>
                          {m.display_name || m.handle || 'Unknown'}
                        </Text>
                        {m.handle && m.display_name && (
                          <Text style={{ color: c.textTertiary, fontSize: 11 }}>@{m.handle}</Text>
                        )}
                      </View>
                      {m.role === 'captain' && <Crown size={12} color={c.accent} />}
                      {m.role === 'co_captain' && <Shield size={12} color={c.accent} />}
                      <Text style={{ color: c.textTertiary, fontSize: 10, fontFamily: TYPO.body.semibold, marginLeft: 4 }}>
                        {m.role === 'captain' ? 'CAPTAIN' : m.role === 'co_captain' ? 'CO-CAPTAIN' : ''}
                      </Text>
                    </View>
                  ))
                )}

                {/* Coach Tools (Assign Workout + Team Analytics) removed — not yet built */}
              </>
            )}
          </View>
        )}
      </View>
    );
  };

  /* ================================================================ */
  /*  MAIN RENDER                                                      */
  /* ================================================================ */

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>TEAMS</Text>
        <TouchableOpacity
          onPress={() => {
            if (!isPro) {
              Platform.OS === 'web'
                ? alert('Creating teams requires Pro. You can still join existing teams.')
                : Alert.alert('Pro Required', 'Creating teams requires Pro. You can still join existing teams.');
              return;
            }
            setShowCreate(true);
          }}
          style={[styles.createBtn, { backgroundColor: c.accent }]}
        >
          <Plus size={16} color="#000" />
        </TouchableOpacity>
      </View>

      {/* Tab bar */}
      <View style={[styles.tabBar, { borderBottomColor: c.border }]}>
        {(['mine', 'rankings', 'browse'] as Tab[]).map(tab => {
          const label = tab === 'mine' ? 'MY TEAMS' : tab === 'rankings' ? 'RANKINGS' : 'BROWSE';
          const Icon = tab === 'mine' ? Shield : tab === 'rankings' ? Trophy : Search;
          const active = activeTab === tab;
          return (
            <TouchableOpacity
              key={tab}
              style={[styles.tabBtn, active && { borderBottomColor: c.accent, borderBottomWidth: 2 }]}
              onPress={() => setActiveTab(tab)}
              activeOpacity={0.7}
            >
              <Icon size={13} color={active ? c.accent : c.textTertiary} />
              <Text style={[styles.tabLabel, { color: active ? c.accent : c.textTertiary, fontFamily: active ? TYPO.heading.bold : TYPO.body.regular }]}>
                {label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* ---- MY TEAMS Tab ---- */}
      {activeTab === 'mine' && (
        <ScrollView
          contentContainerStyle={styles.content}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
        >
          {loading ? (
            <>
              {[1, 2, 3].map(i => (
                <View key={i} style={[styles.skeletonRow, { backgroundColor: c.surface, borderColor: c.border }]} />
              ))}
            </>
          ) : myTeams.length === 0 ? (
            <View style={[styles.empty, { borderColor: c.border }]}>
              <Shield size={32} color={c.textTertiary} />
              <Text style={[styles.emptyTitle, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>NO TEAMS YET</Text>
              <Text style={[styles.emptyDesc, { color: c.textTertiary }]}>
                Create a team or join one from Browse
              </Text>
              <TouchableOpacity
                style={[styles.createTeamEmptyBtn, { backgroundColor: c.accent }]}
                onPress={() => {
                  if (!isPro) {
                    Platform.OS === 'web'
                      ? alert('Creating teams requires Pro.')
                      : Alert.alert('Pro Required', 'Creating teams requires Pro.');
                    return;
                  }
                  setShowCreate(true);
                }}
                activeOpacity={0.7}
              >
                <Plus size={14} color="#000" />
                <Text style={{ color: '#000', fontFamily: TYPO.heading.bold, fontSize: 13, letterSpacing: 1 }}>CREATE TEAM</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <Text style={[styles.sectionTitle, { color: c.textSecondary }]}>MY TEAMS</Text>
              {myTeams.map(renderMyTeamCard)}

              <TouchableOpacity
                style={[styles.createTeamEmptyBtn, { backgroundColor: c.accent, marginTop: SPACE.md }]}
                onPress={() => {
                  if (!isPro) {
                    Platform.OS === 'web'
                      ? alert('Creating teams requires Pro.')
                      : Alert.alert('Pro Required', 'Creating teams requires Pro.');
                    return;
                  }
                  setShowCreate(true);
                }}
                activeOpacity={0.7}
              >
                <Plus size={14} color="#000" />
                <Text style={{ color: '#000', fontFamily: TYPO.heading.bold, fontSize: 13, letterSpacing: 1 }}>CREATE NEW TEAM</Text>
              </TouchableOpacity>
            </>
          )}
        </ScrollView>
      )}

      {/* ---- RANKINGS Tab ---- */}
      {activeTab === 'rankings' && (
        <FlatList
          data={rankings}
          keyExtractor={item => item.team_id}
          renderItem={renderRankingItem}
          contentContainerStyle={[styles.content, { paddingBottom: 40 }]}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
          ListHeaderComponent={
            <View style={{ marginBottom: SPACE.sm }}>
              <Text style={[styles.sectionTitle, { color: c.textSecondary }]}>GLOBAL TEAM RANKINGS</Text>
              <Text style={{ color: c.textTertiary, fontSize: 11, marginTop: 2 }}>
                Ranked by top-10 member average strength
              </Text>
            </View>
          }
          ListEmptyComponent={
            loadingRankings ? (
              <View style={{ padding: 40, alignItems: 'center' }}>
                <ActivityIndicator color={c.accent} />
              </View>
            ) : (
              <View style={[styles.empty, { borderColor: c.border }]}>
                <Trophy size={32} color={c.textTertiary} />
                <Text style={[styles.emptyTitle, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>NO RANKINGS YET</Text>
                <Text style={[styles.emptyDesc, { color: c.textTertiary }]}>
                  Create teams and start lifting to appear here
                </Text>
              </View>
            )
          }
        />
      )}

      {/* ---- BROWSE Tab ---- */}
      {activeTab === 'browse' && (
        <ScrollView
          contentContainerStyle={styles.content}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
        >
          <View style={[styles.searchWrap, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Search size={14} color={c.textTertiary} />
            <TextInput
              style={[styles.searchInput, { color: c.text }]}
              value={query}
              onChangeText={setQuery}
              placeholder="Search by name or tag…"
              placeholderTextColor={c.textTertiary}
            />
          </View>

          {loading ? (
            <>
              {[1, 2, 3].map(i => (
                <View key={i} style={[styles.skeletonRow, { backgroundColor: c.surface, borderColor: c.border }]} />
              ))}
            </>
          ) : browseTeams.filter(t => !myTeamIds.has(t.id)).length === 0 ? (
            <View style={[styles.empty, { borderColor: c.border }]}>
              <Users size={32} color={c.textTertiary} />
              <Text style={[styles.emptyTitle, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>
                {query ? 'NO RESULTS' : 'NO TEAMS YET'}
              </Text>
              <Text style={[styles.emptyDesc, { color: c.textTertiary }]}>
                {query ? 'Try a different search' : 'Be the first to build a crew'}
              </Text>
              {!query && (
                <TouchableOpacity
                  style={[styles.createTeamEmptyBtn, { backgroundColor: c.accent }]}
                  onPress={() => {
                    if (!isPro) {
                      Platform.OS === 'web'
                        ? alert('Creating teams requires Pro.')
                        : Alert.alert('Pro Required', 'Creating teams requires Pro.');
                      return;
                    }
                    setShowCreate(true);
                  }}
                  activeOpacity={0.7}
                >
                  <Plus size={14} color="#000" />
                  <Text style={{ color: '#000', fontFamily: TYPO.heading.bold, fontSize: 13, letterSpacing: 1 }}>CREATE TEAM</Text>
                </TouchableOpacity>
              )}
            </View>
          ) : (
            browseTeams.filter(t => !myTeamIds.has(t.id)).map(t => renderBrowseTeamCard(t))
          )}
        </ScrollView>
      )}

      {/* ================================================================ */}
      {/* Team Detail Modal                                                */}
      {/* ================================================================ */}
      <Modal visible={!!detailTeam} transparent animationType="slide" onRequestClose={closeDetail}>
        <View style={[styles.detailOverlay, { backgroundColor: 'rgba(0,0,0,0.7)' }]}>
          <View style={[styles.detailSheet, { backgroundColor: c.background, borderColor: c.border }]}>
            {/* Handle */}
            <View style={[styles.detailHandle, { backgroundColor: c.border }]} />

            {/* Header */}
            <View style={styles.detailHeader}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.detailTitle, { color: c.text, fontFamily: TYPO.heading.bold }]} numberOfLines={2}>
                  {(detailTeam as any)?.team_name ?? (detailTeam as any)?.name ?? '—'}
                </Text>
                {(detailTeam as any)?.description ? (
                  <Text style={{ color: c.textSecondary, fontSize: 12, marginTop: 4, lineHeight: 18 }}>
                    {(detailTeam as any).description}
                  </Text>
                ) : null}
              </View>
              <TouchableOpacity onPress={closeDetail} style={styles.closeBtn}>
                <Text style={{ color: c.textSecondary, fontSize: 22, lineHeight: 24 }}>×</Text>
              </TouchableOpacity>
            </View>

            {/* Actions */}
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 4 }}>
              <TouchableOpacity
                style={[styles.detailActionBtn, { backgroundColor: c.accent }]}
                onPress={() => shareInviteLink(inviteLink, (detailTeam as any)?.team_name ?? (detailTeam as any)?.name)}
                activeOpacity={0.8}
              >
                <Share2 size={14} color="#000" />
                <Text style={{ color: '#000', fontFamily: TYPO.heading.bold, fontSize: 12, letterSpacing: 0.5 }}>SHARE INVITE</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.detailActionBtn, { backgroundColor: c.surface, borderWidth: 1, borderColor: c.border }]}
                onPress={() => {
                  const myTeam = myTeams[0];
                  if (!myTeam) {
                    Alert.alert('No Team', 'You need to be on a team to issue a challenge.');
                    return;
                  }
                  const targetId = (detailTeam as any)?.id;
                  if (!targetId || targetId === myTeam.id) {
                    Alert.alert('Invalid', 'Cannot challenge your own team.');
                    return;
                  }
                  if (Platform.OS === 'ios') {
                    Alert.prompt(
                      'Challenge Duration',
                      'How many days should this challenge last?',
                      async (input) => {
                        const days = parseInt(input ?? '', 10);
                        if (!days || days < 1 || days > 90) {
                          Alert.alert('Invalid', 'Enter a number between 1 and 90.');
                          return;
                        }
                        const ok = await challengeTeam(myTeam.id, targetId, 'Head-to-Head Challenge', days, profile!.id);
                        Alert.alert(ok ? 'Challenge Sent! 🥊' : 'Error', ok ? 'Your challenge has been sent.' : 'Failed to send challenge. Try again.');
                      },
                      'plain-text',
                      '7',
                      'number-pad',
                    );
                  } else {
                    // Android / web: simple fixed 7-day challenge
                    Alert.alert(
                      'Issue 7-Day Challenge?',
                      `Challenge ${(detailTeam as any)?.team_name ?? (detailTeam as any)?.name ?? 'this team'} to a 7-day head-to-head?`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        {
                          text: 'Challenge!',
                          onPress: async () => {
                            const ok = await challengeTeam(myTeam.id, targetId, 'Head-to-Head Challenge', 7, profile!.id);
                            Alert.alert(ok ? 'Challenge Sent! 🥊' : 'Error', ok ? 'Your challenge has been sent.' : 'Failed to send challenge. Try again.');
                          },
                        },
                      ],
                    );
                  }
                }}
                activeOpacity={0.8}
              >
                <Swords size={14} color={c.accent} />
                <Text style={{ color: c.accent, fontFamily: TYPO.heading.bold, fontSize: 12, letterSpacing: 0.5 }}>CHALLENGE</Text>
              </TouchableOpacity>
            </View>

            {/* Member Strength Leaderboard */}
            <Text style={[styles.sectionTitle, { color: c.textSecondary, marginTop: SPACE.md }]}>
              MEMBER STRENGTH RANKINGS
            </Text>

            <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
              {loadingDetail ? (
                <>
                  {[1, 2, 3, 4].map(i => (
                    <View key={i} style={[styles.skeletonRow, { backgroundColor: c.surface, borderColor: c.border, marginBottom: 6 }]} />
                  ))}
                </>
              ) : detailMembers.length === 0 ? (
                <View style={[styles.empty, { borderColor: c.border, padding: 24 }]}>
                  <Users size={24} color={c.textTertiary} />
                  <Text style={{ color: c.textTertiary, fontSize: 12 }}>No members found</Text>
                </View>
              ) : (
                detailMembers.map((m, idx) => {
                  const rank = idx + 1;
                  const mc = MEDAL_COLORS[rank];
                  return (
                    <View key={m.profile_id} style={[styles.memberRow, { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: c.border + '40' }]}>
                      <Text style={[styles.rankNum, { color: mc || c.textTertiary, fontFamily: TYPO.heading.bold, width: 28, fontSize: 13 }]}>
                        {rank}
                      </Text>
                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                          <Text style={{ color: c.text, fontSize: 13, fontFamily: TYPO.body.semibold }}>
                            {m.display_name || m.handle || 'Unknown'}
                          </Text>
                          {m.role === 'captain' && <Crown size={11} color={c.accent} />}
                          {m.role === 'co_captain' && <Shield size={11} color={c.textSecondary} />}
                        </View>
                        {m.handle && m.display_name && (
                          <Text style={{ color: c.textTertiary, fontSize: 11 }}>@{m.handle}</Text>
                        )}
                      </View>
                      <Text style={[{ color: mc || c.text, fontFamily: TYPO.heading.bold, fontSize: 14 }]}>
                        {m.strength_score > 0 ? m.strength_score : '—'}
                      </Text>
                    </View>
                  );
                })
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* ================================================================ */}
      {/* Create Team Modal                                                */}
      {/* ================================================================ */}
      <Modal visible={showCreate} transparent animationType="fade" onRequestClose={closeCreateModal}>
        <Pressable style={styles.overlay} onPress={closeCreateModal}>
          <Pressable
            style={[styles.modal, { backgroundColor: c.surface, borderColor: c.border }]}
            onPress={e => e.stopPropagation()}
          >
            {createdInviteLink ? (
              /* ---- Success: show invite link ---- */
              <>
                <View style={{ alignItems: 'center', gap: SPACE.sm }}>
                  <Trophy size={32} color={c.accent} />
                  <Text style={[styles.modalTitle, { color: c.text, fontFamily: TYPO.heading.bold }]}>TEAM CREATED!</Text>
                  <Text style={{ color: c.textSecondary, fontSize: 13, textAlign: 'center' }}>
                    Share this link to invite members:
                  </Text>
                </View>

                <View style={[styles.inviteLinkBox, { backgroundColor: c.background, borderColor: c.border }]}>
                  <Text style={{ color: c.accent, fontSize: 12, fontFamily: TYPO.body.semibold }} selectable numberOfLines={2}>
                    {createdInviteLink}
                  </Text>
                </View>

                <TouchableOpacity
                  style={[styles.createTeamBtn, { backgroundColor: c.accent }]}
                  onPress={() => shareInviteLink(createdInviteLink, newName)}
                  activeOpacity={0.8}
                >
                  <Share2 size={14} color="#000" />
                  <Text style={{ color: '#000', fontFamily: TYPO.heading.bold, fontSize: 13, letterSpacing: 1 }}>SHARE INVITE LINK</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.createTeamBtn, { backgroundColor: c.surface, borderWidth: 1, borderColor: c.border, marginTop: 0 }]}
                  onPress={closeCreateModal}
                  activeOpacity={0.8}
                >
                  <Text style={{ color: c.textSecondary, fontFamily: TYPO.heading.bold, fontSize: 13, letterSpacing: 1 }}>DONE</Text>
                </TouchableOpacity>
              </>
            ) : (
              /* ---- Form ---- */
              <>
                <Text style={[styles.modalTitle, { color: c.text, fontFamily: TYPO.heading.bold }]}>CREATE TEAM</Text>

                <Text style={[styles.inputLabel, { color: c.textSecondary }]}>TEAM NAME</Text>
                <TextInput
                  style={[styles.input, { color: c.text, borderColor: c.border, backgroundColor: c.background }]}
                  value={newName}
                  onChangeText={setNewName}
                  placeholder="Iron Warriors"
                  placeholderTextColor={c.textTertiary}
                  maxLength={30}
                />

                <Text style={[styles.inputLabel, { color: c.textSecondary }]}>TAG (optional, 5 chars max)</Text>
                <TextInput
                  style={[styles.input, { color: c.text, borderColor: c.border, backgroundColor: c.background }]}
                  value={newTag}
                  onChangeText={setNewTag}
                  placeholder="IRON"
                  placeholderTextColor={c.textTertiary}
                  maxLength={5}
                  autoCapitalize="characters"
                />

                <Text style={[styles.inputLabel, { color: c.textSecondary }]}>DESCRIPTION (optional)</Text>
                <TextInput
                  style={[styles.input, styles.inputMulti, { color: c.text, borderColor: c.border, backgroundColor: c.background }]}
                  value={newDesc}
                  onChangeText={setNewDesc}
                  placeholder="What's your team about?"
                  placeholderTextColor={c.textTertiary}
                  multiline
                  maxLength={200}
                />

                <TouchableOpacity
                  style={[styles.createTeamBtn, { backgroundColor: newName.trim() ? c.accent : c.surfaceVariant }]}
                  onPress={handleCreate}
                  disabled={!newName.trim() || creating}
                  activeOpacity={0.8}
                >
                  {creating ? (
                    <ActivityIndicator color="#000" />
                  ) : (
                    <Text style={{ color: newName.trim() ? '#000' : c.textTertiary, fontFamily: TYPO.heading.bold, fontSize: 14, letterSpacing: 1 }}>
                      CREATE
                    </Text>
                  )}
                </TouchableOpacity>
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

/* ================================================================ */
/*  Styles                                                           */
/* ================================================================ */

const styles = StyleSheet.create({
  /* header */
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 12 },
  backBtn: { padding: 4 },
  title: { fontSize: FONT.xl, letterSpacing: 1, flex: 1 },
  createBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },

  /* tabs */
  tabBar: { flexDirection: 'row', borderBottomWidth: 1, paddingHorizontal: 4 },
  tabBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 10, paddingBottom: 9 },
  tabLabel: { fontSize: 11, letterSpacing: 1 },

  /* content */
  content: { padding: 16, gap: SPACE.sm, paddingBottom: 40 },
  sectionTitle: { fontSize: 11, letterSpacing: 1.5, marginBottom: 4 },

  /* skeleton */
  skeletonRow: { height: 64, borderWidth: 1, borderRadius: RADIUS.xs, opacity: 0.4 },

  /* my teams */
  myTeamCard: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderRadius: RADIUS.xs, padding: 14, gap: 10 },
  myTeamName: { fontSize: FONT.base },

  /* rankings */
  rankRow: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: RADIUS.xs, padding: 12, marginBottom: 6 },
  rankBadge: { width: 32, height: 32, borderRadius: 16, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  rankNum: { fontSize: 14 },
  rankTeamName: { fontSize: 14 },
  rankScore: { fontSize: 18 },

  /* browse */
  searchWrap: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: RADIUS.xs, paddingHorizontal: 12, marginBottom: 4 },
  searchInput: { flex: 1, paddingVertical: 10, fontSize: 14 },
  teamCard: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 14, gap: SPACE.xs },
  teamHeader: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  teamName: { fontSize: FONT.base },
  teamDesc: { fontSize: 12, lineHeight: 18 },
  actionBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderRadius: RADIUS.xs, paddingVertical: 4, paddingHorizontal: 10 },
  membersList: { borderTopWidth: 1, marginTop: 8, paddingTop: 8, gap: 6 },
  memberRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 4, gap: 4 },
  statBox: { flex: 1, borderWidth: 1, borderRadius: RADIUS.xs, padding: 10, alignItems: 'center', gap: 2 },

  /* empty */
  empty: { padding: 40, alignItems: 'center', gap: SPACE.md, borderWidth: 1, borderRadius: RADIUS.xs, borderStyle: 'dashed' },
  emptyTitle: { fontSize: 14, letterSpacing: 1 },
  emptyDesc: { fontSize: 12, textAlign: 'center' },
  createTeamEmptyBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 12, paddingHorizontal: 20, borderRadius: RADIUS.xs, marginTop: 4 },

  /* detail modal */
  detailOverlay: { flex: 1, justifyContent: 'flex-end' },
  detailSheet: { maxHeight: '85%', borderTopLeftRadius: RADIUS.lg ?? 16, borderTopRightRadius: RADIUS.lg ?? 16, borderWidth: 1, padding: 20, paddingTop: 12, gap: SPACE.sm },
  detailHandle: { width: 36, height: 4, borderRadius: 2, alignSelf: 'center', marginBottom: 8 },
  detailHeader: { flexDirection: 'row', gap: 12, alignItems: 'flex-start' },
  detailTitle: { fontSize: FONT.lg ?? 18, letterSpacing: 0.5 },
  closeBtn: { padding: 4 },
  detailActionBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 12, borderRadius: RADIUS.xs },

  /* create modal */
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center' },
  modal: { width: '85%', maxWidth: 360, borderWidth: 1, borderRadius: RADIUS.xs, padding: 24, gap: SPACE.md },
  modalTitle: { fontSize: FONT.lg, letterSpacing: 1 },
  inputLabel: { fontSize: 11, letterSpacing: 1 },
  input: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 12, fontSize: 14 },
  inputMulti: { minHeight: 70, textAlignVertical: 'top' },
  createTeamBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 14, borderRadius: RADIUS.xs, marginTop: 4 },
  inviteLinkBox: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 12, borderStyle: 'dashed' },
});
